(function bootstrapCity2PlusFreeBattery(global) {
  'use strict';

  const VERSION = '2026-10-03.9';
  const BLACK_VARIANT_ID = '56226951954598';
  const SILVER_VARIANT_ID = '56226951987366';
  const GIFT_VARIANT_ID = '56316922921126';
  const CHAIR_VARIANT_IDS = Object.freeze([BLACK_VARIANT_ID, SILVER_VARIANT_ID]);
  const PROTECTED_VARIANT_IDS = Object.freeze([...CHAIR_VARIANT_IDS, GIFT_VARIANT_ID]);
  const GIFT_PROPERTY = '_cm_city2plus_gen3_gift';
  const GIFT_PROPERTY_VALUE = '1';
  const MAX_SYNC_PASSES = 6;
  const SYNC_DEBOUNCE_MS = 120;
  const CHECKING_MESSAGE = 'Checking your included CITY 2 PLUS Gen III battery...';

  function normalizedIntegerString(value, allowZero) {
    if (typeof value === 'number') {
      if (!Number.isSafeInteger(value) || value < (allowZero ? 0 : 1)) return '';
      return String(value);
    }

    if (typeof value !== 'string') return '';
    if (!/^(?:0|[1-9]\d*)$/.test(value)) return '';
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < (allowZero ? 0 : 1)) return '';
    return value;
  }

  function variantId(item) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return '';
    const hasVariantId = Object.prototype.hasOwnProperty.call(item, 'variant_id');
    const hasId = Object.prototype.hasOwnProperty.call(item, 'id');
    const normalizedVariantId = normalizedIntegerString(item.variant_id, false);
    const normalizedId = normalizedIntegerString(item.id, false);

    if (hasVariantId && !normalizedVariantId) return '';
    if (hasId && !normalizedId) return '';
    if (
      normalizedVariantId &&
      normalizedId &&
      normalizedVariantId !== normalizedId
    ) {
      return '';
    }

    return normalizedVariantId || normalizedId;
  }

  function positiveQuantity(value) {
    const normalized = normalizedIntegerString(value, true);
    if (!normalized) return 0;
    const quantity = Number(normalized);
    return quantity > 0 ? quantity : 0;
  }

  function moneyIsZero(value) {
    let amount;

    if (typeof value === 'number') {
      amount = value;
    } else if (
      typeof value === 'string' &&
      /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value)
    ) {
      amount = Number(value);
    } else {
      return false;
    }

    return Number.isFinite(amount) && amount === 0;
  }

  function moneyIsFinite(value) {
    if (typeof value === 'number') return Number.isFinite(value);
    return (
      typeof value === 'string' &&
      /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value) &&
      Number.isFinite(Number(value))
    );
  }

  function cartSchemaIsValid(cart) {
    if (!cart || typeof cart !== 'object' || Array.isArray(cart)) return false;
    if (!Array.isArray(cart.items)) return false;

    return cart.items.every(
      (item) =>
        item &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        variantId(item) !== '' &&
        normalizedIntegerString(item.quantity, false) !== ''
    );
  }

  function isTaggedGift(item) {
    return (
      variantId(item) === GIFT_VARIANT_ID &&
      item &&
      item.properties &&
      String(item.properties[GIFT_PROPERTY]) === GIFT_PROPERTY_VALUE
    );
  }

  function analyzeCart(cart) {
    const schemaValid = cartSchemaIsValid(cart);
    const items = schemaValid ? cart.items : [];
    const qualifyingItems = items.filter((item) => CHAIR_VARIANT_IDS.includes(variantId(item)));
    const giftItems = items.filter((item) => variantId(item) === GIFT_VARIANT_ID);
    const taggedGiftItems = items.filter(isTaggedGift);
    const paidBatteryItems = items.filter(
      (item) => variantId(item) === GIFT_VARIANT_ID && !isTaggedGift(item)
    );
    const qualifyingQuantity = qualifyingItems.reduce(
      (total, item) => total + positiveQuantity(item.quantity),
      0
    );
    const giftQuantity = giftItems.reduce(
      (total, item) => total + positiveQuantity(item.quantity),
      0
    );
    const taggedGiftQuantity = taggedGiftItems.reduce(
      (total, item) => total + positiveQuantity(item.quantity),
      0
    );
    const paidBatteryQuantity = paidBatteryItems.reduce(
      (total, item) => total + positiveQuantity(item.quantity),
      0
    );
    const freeGiftItems = giftItems.filter(
      (item) => moneyIsZero(item.final_price) && moneyIsZero(item.final_line_price)
    );
    const freeGiftQuantity = freeGiftItems.reduce(
      (total, item) => total + positiveQuantity(item.quantity),
      0
    );
    const taggedGiftIsFree =
      taggedGiftItems.length > 0 &&
      taggedGiftItems.every(
        (item) => moneyIsZero(item.final_price) && moneyIsZero(item.final_line_price)
      );
    const giftPriceEvidenceIsValid = giftItems.every(
      (item) => moneyIsFinite(item.final_price) && moneyIsFinite(item.final_line_price)
    );
    const freeGiftQuantityIsExact =
      qualifyingQuantity > 0 && freeGiftQuantity === qualifyingQuantity;
    const noPromotionLinesRemain = qualifyingQuantity === 0 && taggedGiftQuantity === 0;
    const promotionIsExact =
      qualifyingQuantity > 0 &&
      taggedGiftQuantity === qualifyingQuantity &&
      giftPriceEvidenceIsValid &&
      freeGiftQuantityIsExact;

    return {
      cart,
      schemaValid,
      items,
      qualifyingItems,
      qualifyingQuantity,
      giftItems,
      giftQuantity,
      taggedGiftItems,
      taggedGiftQuantity,
      paidBatteryItems,
      paidBatteryQuantity,
      freeGiftItems,
      freeGiftQuantity,
      taggedGiftIsFree,
      giftPriceEvidenceIsValid,
      freeGiftQuantityIsExact,
      checkoutSafe: schemaValid && (noPromotionLinesRemain || promotionIsExact),
      promotionIsExact
    };
  }

  function updatePlanForTaggedLines(taggedGiftItems, desiredQuantity) {
    const updates = {};
    let missingKey = false;

    taggedGiftItems.forEach((item, index) => {
      if (!item.key) {
        missingKey = true;
        return;
      }
      updates[item.key] = index === 0 ? desiredQuantity : 0;
    });

    if (missingKey) {
      return {
        kind: 'error',
        code: 'tagged-line-key-missing'
      };
    }

    return {
      kind: 'update',
      updates
    };
  }

  function cartIntentIsAmbiguous(analysis, previousSnapshot, trustPreviousSnapshot) {
    if (!analysis || analysis.qualifyingQuantity === 0) return false;

    if (trustPreviousSnapshot && previousSnapshot) {
      const taggedQuantityChanged =
        analysis.taggedGiftQuantity !==
        positiveQuantity(previousSnapshot.taggedGiftQuantity);
      const untaggedBatteryWasOrIsPresent =
        analysis.paidBatteryQuantity > 0 ||
        positiveQuantity(previousSnapshot.untaggedGiftQuantity) > 0;

      return taggedQuantityChanged && untaggedBatteryWasOrIsPresent;
    }

    return (
      analysis.paidBatteryQuantity > 0 &&
      analysis.taggedGiftQuantity !== analysis.qualifyingQuantity
    );
  }

  function protectMutation(plan, allowMutation) {
    if (allowMutation || plan.kind === 'none' || plan.kind === 'wait-for-discount') {
      return plan;
    }

    return {
      kind: 'error',
      code: 'ambiguous-cart-intent'
    };
  }

  function planCartMutation(analysis, options) {
    const allowReprice = Boolean(options && options.allowReprice);
    const allowMutation = !(options && options.allowMutation === false);

    if (!analysis || analysis.schemaValid !== true) {
      return {
        kind: 'error',
        code: 'cart-schema-invalid'
      };
    }

    if (analysis.qualifyingQuantity > 0 && !analysis.giftPriceEvidenceIsValid) {
      return {
        kind: 'error',
        code: 'gift-price-evidence-invalid'
      };
    }

    if (analysis.qualifyingQuantity === 0) {
      if (analysis.taggedGiftItems.length === 0) return { kind: 'none' };
      return protectMutation(
        updatePlanForTaggedLines(analysis.taggedGiftItems, 0),
        allowMutation
      );
    }

    if (analysis.taggedGiftItems.length === 0) {
      return protectMutation(
        {
          kind: 'add',
          variantId: GIFT_VARIANT_ID,
          quantity: analysis.qualifyingQuantity,
          properties: {
            [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE
          }
        },
        allowMutation
      );
    }

    if (analysis.taggedGiftQuantity !== analysis.qualifyingQuantity) {
      return protectMutation(
        updatePlanForTaggedLines(
          analysis.taggedGiftItems,
          analysis.qualifyingQuantity
        ),
        allowMutation
      );
    }

    if (!analysis.freeGiftQuantityIsExact) {
      if (allowReprice && allowMutation) {
        const repricePlan = updatePlanForTaggedLines(
          analysis.taggedGiftItems,
          analysis.qualifyingQuantity
        );
        if (repricePlan.kind === 'error') return repricePlan;
        return {
          ...repricePlan,
          kind: 'reprice'
        };
      }
      return {
        kind: 'wait-for-discount',
        code: 'tagged-gift-not-free'
      };
    }

    return { kind: 'none' };
  }

  function mutationRequestForPlan(plan, sectionPayload) {
    const sections = sectionPayload && typeof sectionPayload === 'object' ? sectionPayload : {};

    if (plan.kind === 'add') {
      const properties = plan.properties && typeof plan.properties === 'object'
        ? plan.properties
        : {};
      const isExactTaggedGift =
        String(plan.variantId) === GIFT_VARIANT_ID &&
        String(properties[GIFT_PROPERTY]) === GIFT_PROPERTY_VALUE;

      if (!isExactTaggedGift) {
        throw new Error('Only the exact tagged promotional battery may be added.');
      }

      return {
        path: 'cart/add.js',
        body: {
          items: [
            {
              id: Number(plan.variantId),
              quantity: plan.quantity,
              properties
            }
          ],
          ...sections
        }
      };
    }

    if (plan.kind === 'update' || plan.kind === 'reprice') {
      return {
        path: 'cart/update.js',
        body: {
          updates: plan.updates,
          ...sections
        }
      };
    }

    throw new Error('Unsupported CITY 2 PLUS gift-cart operation.');
  }

  async function mutateThenRefetch(plan, mutationFunction, cartFetcher) {
    await mutationFunction(plan);
    return cartFetcher();
  }

  function errorMessage(error) {
    const reason = error && error.message ? ' ' + error.message : '';
    return (
      'We could not confirm your included CITY 2 PLUS Gen III battery at C$0.' +
      reason +
      ' Checkout is paused; please refresh or contact Care Made.'
    );
  }

  function gateScheduledAuthoritativeSync(lockFunction) {
    lockFunction(CHECKING_MESSAGE, 'checking');
  }

  function failClosedAfterSyncError(error, lockFunction) {
    lockFunction(errorMessage(error), 'error');
  }

  function markQueuedIntent(stateObject) {
    stateObject.queued = true;
    stateObject.ambiguousIntent = true;
  }

  function claimSingleMutation(syncAttempt) {
    if (!syncAttempt || syncAttempt.mutationPerformed) return false;
    syncAttempt.mutationPerformed = true;
    return true;
  }

  function stableSerialize(value) {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) {
      return '[' + value.map((entry) => stableSerialize(entry)).join(',') + ']';
    }
    return (
      '{' +
      Object.keys(value)
        .sort()
        .map((key) => JSON.stringify(key) + ':' + stableSerialize(value[key]))
        .join(',') +
      '}'
    );
  }

  function protectedStructureSignature(analysis) {
    if (!analysis || analysis.schemaValid !== true) return '';
    return analysis.items
      .filter((item) => PROTECTED_VARIANT_IDS.includes(variantId(item)))
      .map((item) =>
        stableSerialize({
          variantId: variantId(item),
          key: typeof item.key === 'string' ? item.key : '',
          quantity: positiveQuantity(item.quantity),
          tagged: isTaggedGift(item),
          properties:
            item.properties && typeof item.properties === 'object'
              ? item.properties
              : {},
          sellingPlan:
            item.selling_plan_allocation &&
            typeof item.selling_plan_allocation === 'object'
              ? item.selling_plan_allocation.selling_plan ||
                item.selling_plan_allocation.selling_plan_id ||
                null
              : item.selling_plan || null
        })
      )
      .sort()
      .join('|');
  }

  function queueUnverifiedRenderedCartReview(stateObject, reason, lockFunction) {
    if (!stateObject || !stateObject.busy) return false;
    gateScheduledAuthoritativeSync(lockFunction);
    stateObject.queuedUnverified = true;
    stateObject.queuedUnverifiedReason = reason;
    return true;
  }

  const testApi = Object.freeze({
    VERSION,
    BLACK_VARIANT_ID,
    SILVER_VARIANT_ID,
    GIFT_VARIANT_ID,
    CHAIR_VARIANT_IDS,
    PROTECTED_VARIANT_IDS,
    GIFT_PROPERTY,
    GIFT_PROPERTY_VALUE,
    moneyIsZero,
    cartSchemaIsValid,
    analyzeCart,
    planCartMutation,
    cartIntentIsAmbiguous,
    mutationRequestForPlan,
    mutateThenRefetch,
    gateScheduledAuthoritativeSync,
    failClosedAfterSyncError,
    markQueuedIntent,
    claimSingleMutation,
    protectedStructureSignature,
    queueUnverifiedRenderedCartReview,
    isTaggedGift
  });

  if (typeof module === 'object' && module.exports) {
    module.exports = testApi;
    return;
  }

  if (!global || !global.document) return;
  if (global.__cmCity2PlusFreeBattery) return;

  const document = global.document;
  const state = {
    version: VERSION,
    busy: false,
    queued: false,
    ambiguousIntent: false,
    queuedUnverified: false,
    queuedUnverifiedReason: null, failedReadRetries: 0,
    locked: false,
    timer: null,
    lastKnownPromotion: false,
    authoritativeReadComplete: false,
    lastVerifiedCart: null,
    observers: new Map(),
    controlSnapshots: new Map(),
    acceleratedSnapshots: new Map(),
    test: testApi
  };

  global.__cmCity2PlusFreeBattery = state;

  function localeRoot() {
    const configuredRoot =
      global.Shopify && global.Shopify.routes && global.Shopify.routes.root;
    const root = typeof configuredRoot === 'string' && configuredRoot ? configuredRoot : '/';
    return root.endsWith('/') ? root : root + '/';
  }

  function ajaxUrl(relativePath) {
    return localeRoot() + relativePath.replace(/^\/+/, '');
  }

  function storefrontContextUrl() {
    return global.location.pathname + global.location.search;
  }

  async function requestJson(url, options) {
    const response = await global.fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options && options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options && options.headers ? options.headers : {})
      },
      ...options
    });
    const text = await response.text();
    let payload;

    try {
      payload = text ? JSON.parse(text) : {};
    } catch (error) {
      throw new Error('Shopify returned a non-JSON cart response.');
    }

    if (!response.ok || payload.status >= 400 || payload.errors) {
      const message =
        payload.description ||
        payload.message ||
        (typeof payload.errors === 'string' ? payload.errors : '') ||
        'Shopify could not update the included battery.';
      throw new Error(message);
    }

    return payload;
  }

  function fetchCart() {
    return requestJson(ajaxUrl('cart.js'), {
      method: 'GET',
      cache: 'no-store'
    });
  }

  function addSectionDescriptor(map, id, targetSelector, sourceSelector) {
    if (!id || !targetSelector || !document.querySelector(targetSelector)) return;
    if (!map.has(id)) map.set(id, []);
    map.get(id).push({ targetSelector, sourceSelector });
  }

  function sectionDescriptors() {
    const map = new Map();

    addSectionDescriptor(map, 'cart-drawer', '#CartDrawer', '#CartDrawer');
    addSectionDescriptor(
      map,
      'cart-icon-bubble',
      '#cart-icon-bubble',
      '#cart-icon-bubble'
    );

    const mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id) {
      addSectionDescriptor(
        map,
        mainItems.dataset.id,
        '#main-cart-items .js-contents',
        '.js-contents'
      );
    }

    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset.id) {
      addSectionDescriptor(
        map,
        mainFooter.dataset.id,
        '#main-cart-footer .js-contents',
        '.js-contents'
      );
      addSectionDescriptor(
        map,
        mainFooter.dataset.id,
        '#main-cart-footer .js-contents-shiping',
        '.js-contents-shiping'
      );
    }

    return map;
  }

  function sectionRequestPayload(descriptors) {
    return {
      sections: Array.from(descriptors.keys()).slice(0, 5),
      sections_url: storefrontContextUrl()
    };
  }

  function rebindDrawerOverlay() {
    const drawer = document.querySelector('cart-drawer');
    const overlay = drawer && drawer.querySelector('#CartDrawer-Overlay');
    if (!drawer || !overlay || overlay.dataset.cmCityGiftBound === 'true') return;
    overlay.dataset.cmCityGiftBound = 'true';
    overlay.addEventListener('click', () => {
      if (typeof drawer.close === 'function') drawer.close();
    });
  }

  function renderSectionBundle(sections, descriptors) {
    if (descriptors.size === 0) return true;
    if (!sections || typeof sections !== 'object') return false;

    let complete = true;

    descriptors.forEach((targets, sectionId) => {
      const html = sections[sectionId];
      if (typeof html !== 'string') {
        complete = false;
        return;
      }

      const parsed = new global.DOMParser().parseFromString(html, 'text/html');
      targets.forEach(({ targetSelector, sourceSelector }) => {
        const target = document.querySelector(targetSelector);
        const source = parsed.querySelector(sourceSelector);
        if (!target || !source) {
          complete = false;
          return;
        }
        target.innerHTML = source.innerHTML;
      });
    });

    rebindDrawerOverlay();
    attachCartObservers();
    if (state.locked) applyCheckoutLock();
    discardObserverRecords();
    return complete;
  }

  async function fetchFreshSections(descriptors) {
    if (descriptors.size === 0) return {};
    const url = new URL(global.location.href);
    url.searchParams.set('sections', Array.from(descriptors.keys()).slice(0, 5).join(','));

    return requestJson(url.pathname + url.search, {
      method: 'GET',
      cache: 'no-store'
    });
  }

  async function renderMutationSections(payload, descriptors) {
    if (renderSectionBundle(payload && payload.sections, descriptors)) return;
    const freshSections = await fetchFreshSections(descriptors);
    if (!renderSectionBundle(freshSections, descriptors)) {
      throw new Error('Cart changed, but Shopify could not refresh every cart surface.');
    }
  }

  async function performMutation(plan) {
    const descriptors = sectionDescriptors();
    const sectionPayload = sectionRequestPayload(descriptors);
    const request = mutationRequestForPlan(plan, sectionPayload);
    const payload = await requestJson(ajaxUrl(request.path), {
      method: 'POST',
      body: JSON.stringify(request.body)
    });

    await renderMutationSections(payload, descriptors);
  }

  function statusElements() {
    return Array.from(document.querySelectorAll('[data-cm-city-gift-status]'));
  }

  function ensureStatusElements() {
    const buttons = Array.from(
      document.querySelectorAll('#CartDrawer-Checkout, #checkout')
    );

    buttons.forEach((button, index) => {
      const row = button.parentElement; const parent = row && row.parentElement ? row.parentElement : row;
      if (!parent || parent.querySelector(':scope > [data-cm-city-gift-status]')) return;
      const status = document.createElement('p');
      status.dataset.cmCityGiftStatus = 'true';
      status.id = 'cm-city-gift-status-' + index;
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.style.margin = '0 0 0.75rem';
      status.style.fontSize = '1.3rem';
      status.style.lineHeight = '1.45';
      status.style.color = 'rgba(var(--color-foreground), 0.75)';
      parent.insertBefore(status, parent === row ? button : row);
    });
  }

  function setStatus(message, tone) {
    ensureStatusElements();
    statusElements().forEach((status) => {
      status.hidden = !message;
      status.textContent = message || '';
      status.dataset.cmCityGiftTone = tone || '';
      if (tone === 'error') {
        status.style.color = '#9f1d1d';
      } else if (tone === 'success') {
        status.style.color = '#17663a';
      } else {
        status.style.color = 'rgba(var(--color-foreground), 0.75)';
      }
    });
    discardObserverRecords();
  }

  function checkoutControls() {
    return Array.from(
      document.querySelectorAll(
        '#CartDrawer-Checkout, #checkout, a[href*="/checkout"]'
      )
    );
  }

  function acceleratedCheckoutContainers() {
    return Array.from(
      document.querySelectorAll(
        [
          '.additional-checkout-buttons',
          '.cart__dynamic-checkout-buttons',
          '.shopify-payment-button',
          '[data-shopify="payment-button"]',
          '[data-shopify-buttoncontainer]',
          'shopify-accelerated-checkout',
          'shopify-accelerated-checkout-cart'
        ].join(', ')
      )
    );
  }

  function applyCheckoutLock() {
    checkoutControls().forEach((control) => {
      if (!state.controlSnapshots.has(control)) {
        state.controlSnapshots.set(control, {
          hasDisabledProperty: 'disabled' in control,
          disabled: 'disabled' in control ? Boolean(control.disabled) : false,
          ariaDisabled: control.getAttribute('aria-disabled'),
          tabIndex: control.getAttribute('tabindex'),
          pointerEvents: control.style.pointerEvents
        });
      }

      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('tabindex', '-1');
      control.style.pointerEvents = 'none';
    });

    acceleratedCheckoutContainers().forEach((container) => {
      if (!state.acceleratedSnapshots.has(container)) {
        state.acceleratedSnapshots.set(container, {
          hidden: container.hidden,
          ariaHidden: container.getAttribute('aria-hidden'),
          inert: container.hasAttribute('inert'),
          pointerEvents: container.style.pointerEvents
        });
      }
      container.hidden = true;
      container.setAttribute('aria-hidden', 'true');
      container.setAttribute('inert', '');
      container.style.pointerEvents = 'none';
    });

    discardObserverRecords();
  }

  function lockCheckout(message, tone) {
    state.locked = true;
    applyCheckoutLock();
    setStatus(message, tone);
  }

  function releaseCheckoutAfterVerifiedCart(message) {
    state.locked = false;

    state.controlSnapshots.forEach((snapshot, control) => {
      if (!control.isConnected) return;
      if (snapshot.hasDisabledProperty) control.disabled = snapshot.disabled;
      if (snapshot.ariaDisabled == null) {
        control.removeAttribute('aria-disabled');
      } else {
        control.setAttribute('aria-disabled', snapshot.ariaDisabled);
      }
      if (snapshot.tabIndex == null) {
        control.removeAttribute('tabindex');
      } else {
        control.setAttribute('tabindex', snapshot.tabIndex);
      }
      control.style.pointerEvents = snapshot.pointerEvents;
    });
    state.controlSnapshots.clear();

    state.acceleratedSnapshots.forEach((snapshot, container) => {
      if (!container.isConnected) return;
      container.hidden = snapshot.hidden;
      if (snapshot.ariaHidden == null) {
        container.removeAttribute('aria-hidden');
      } else {
        container.setAttribute('aria-hidden', snapshot.ariaHidden);
      }
      if (snapshot.inert) {
        container.setAttribute('inert', '');
      } else {
        container.removeAttribute('inert');
      }
      container.style.pointerEvents = snapshot.pointerEvents;
    });
    state.acceleratedSnapshots.clear();

    setStatus(message || '', message ? 'success' : '');
  }

  async function confirmQueuedRenderedCart(cart) {
    let confirmedCart = cart;
    let confirmationReads = 0;

    while (state.queuedUnverified) {
      if (confirmationReads >= MAX_SYNC_PASSES) {
        throw new Error('The cart drawer did not reach a stable rendered state.');
      }

      const beforeAnalysis = analyzeCart(confirmedCart);
      if (!beforeAnalysis.schemaValid) {
        state.authoritativeReadComplete = false;
        throw new Error('Shopify returned incomplete or invalid cart data.');
      }
      const beforeSignature = protectedStructureSignature(beforeAnalysis);

      state.queuedUnverified = false;
      state.queuedUnverifiedReason = null;
      const nextCart = await fetchCart();
      const afterAnalysis = analyzeCart(nextCart);
      if (!afterAnalysis.schemaValid) {
        state.authoritativeReadComplete = false;
        throw new Error('Shopify returned incomplete or invalid cart data.');
      }
      state.authoritativeReadComplete = true;

      if (beforeSignature !== protectedStructureSignature(afterAnalysis)) {
        state.ambiguousIntent = true;
        throw new Error(
          'The protected cart structure changed before verification finished.'
        );
      }

      confirmedCart = nextCart;
      confirmationReads += 1;
    }

    return confirmedCart;
  }

  async function synchronizeCart(reason) {
    if (state.busy) {
      markQueuedIntent(state);
      return;
    }

    state.busy = true;
    state.queued = false;
    let repriceAttempted = false;
    let mutationAllowed = null;
    const syncAttempt = { mutationPerformed: false }; let cartReadValid = false;

    try {
      let cart = await fetchCart(); cartReadValid = analyzeCart(cart).schemaValid === true; if (cartReadValid) state.failedReadRetries = 0;

      for (let pass = 0; pass < MAX_SYNC_PASSES; pass += 1) {
        cart = await confirmQueuedRenderedCart(cart);
        const analysis = analyzeCart(cart);
        if (!analysis.schemaValid) {
          state.authoritativeReadComplete = false;
          throw new Error('Shopify returned incomplete or invalid cart data.');
        }
        state.authoritativeReadComplete = true;

        const protectedLinesPresent =
          analysis.qualifyingQuantity > 0 || analysis.giftQuantity > 0;

        if (!protectedLinesPresent) {
          if (state.queued) {
            state.queued = false;
            state.ambiguousIntent = false;
            cart = await fetchCart();
            continue;
          }

          state.ambiguousIntent = false;
          state.lastKnownPromotion = false;
          releaseCheckoutAfterVerifiedCart('');
          state.lastVerifiedCart = {
            qualifyingQuantity: 0,
            giftQuantity: 0,
            taggedGiftQuantity: 0,
            untaggedGiftQuantity: 0
          };
          return;
        }

        if (mutationAllowed == null) {
          const trustPreviousSnapshot =
            reason === 'main-cart-render' || reason === 'cart-drawer-render';
          const ambiguousSnapshotIntent = cartIntentIsAmbiguous(
            analysis,
            state.lastVerifiedCart,
            trustPreviousSnapshot
          );
          mutationAllowed =
            reason !== 'queued-cart-change' &&
            !state.ambiguousIntent &&
            !ambiguousSnapshotIntent;
        }
        const promotionPresent =
          analysis.qualifyingQuantity > 0 || analysis.taggedGiftQuantity > 0;

        state.lastKnownPromotion = promotionPresent;

        if (promotionPresent) {
          lockCheckout('Checking your included CITY 2 PLUS Gen III battery...', 'checking');
        }

        if (!mutationAllowed) {
          state.ambiguousIntent = true;
          throw new Error(
            'The cart changed in a way that could affect a separately paid battery.'
          );
        }

        const plan = planCartMutation(analysis, {
          allowReprice: mutationAllowed && !repriceAttempted,
          allowMutation: mutationAllowed
        });

        if (plan.kind === 'error') {
          if (plan.code === 'cart-schema-invalid') {
            state.authoritativeReadComplete = false;
            throw new Error('Shopify returned incomplete or invalid cart data.');
          }
          if (plan.code === 'gift-price-evidence-invalid') {
            throw new Error('The battery price evidence was missing or invalid.');
          }
          if (plan.code === 'ambiguous-cart-intent') {
            throw new Error(
              'The cart changed in a way that could affect a separately paid battery.'
            );
          }
          throw new Error('The included battery line could not be identified safely.');
        }

        if (plan.kind === 'wait-for-discount') {
          throw new Error(
            'The battery is present, but the automatic 100% discount is not active.'
          );
        }

        if (plan.kind === 'none') {
          if (state.queuedUnverified) {
            cart = await confirmQueuedRenderedCart(cart);
            continue;
          }
          if (state.queued) {
            throw new Error('The cart changed again before verification finished.');
          }

          if (!analysis.checkoutSafe) {
            throw new Error('The included battery quantity or C$0 price is not exact.');
          }

          if (analysis.promotionIsExact) {
            releaseCheckoutAfterVerifiedCart(
              'Included CITY 2 PLUS Gen III battery confirmed at C$0.'
            );
          } else {
            releaseCheckoutAfterVerifiedCart('');
          }

          state.lastVerifiedCart = {
            qualifyingQuantity: analysis.qualifyingQuantity,
            giftQuantity: analysis.giftQuantity,
            taggedGiftQuantity: analysis.taggedGiftQuantity,
            untaggedGiftQuantity: analysis.paidBatteryQuantity
          };

          return;
        }

        lockCheckout(
          plan.kind === 'reprice'
            ? 'Confirming your included CITY 2 PLUS Gen III battery at C$0...'
            : 'Adding your included CITY 2 PLUS Gen III battery...',
          'checking'
        );
        if (state.queuedUnverified) {
          cart = await confirmQueuedRenderedCart(cart);
          continue;
        }
        if (!claimSingleMutation(syncAttempt)) {
          state.ambiguousIntent = true;
          throw new Error(
            'The cart changed unexpectedly after the protected battery update.'
          );
        }
        if (plan.kind === 'reprice') repriceAttempted = true;
        if (state.queued) {
          throw new Error('The cart changed again before verification finished.');
        }
        cart = await mutateThenRefetch(plan, performMutation, fetchCart);
        cart = await confirmQueuedRenderedCart(cart);
        if (state.queued) {
          throw new Error('The cart changed again before verification finished.');
        }
      }

      throw new Error('The cart did not reach a stable included-battery state.');
    } catch (error) {
      failClosedAfterSyncError(error, lockCheckout);
    } finally {
      const queuedUnverifiedReason = state.queuedUnverifiedReason;
      state.busy = false;
      if (state.queued) state.ambiguousIntent = true;
      if (!state.queued && state.queuedUnverified && queuedUnverifiedReason && (cartReadValid || state.failedReadRetries < 1)) { if (!cartReadValid) state.failedReadRetries += 1;
        scheduleSync(queuedUnverifiedReason, null, SYNC_DEBOUNCE_MS);
      }
    }
  }

  function scheduleSync(reason, possibleVariantId, delay) {
    void possibleVariantId;
    gateScheduledAuthoritativeSync(lockCheckout);

    if (state.busy) {
      markQueuedIntent(state);
      return;
    }

    global.clearTimeout(state.timer);
    state.timer = global.setTimeout(
      () => synchronizeCart(reason),
      typeof delay === 'number' ? delay : SYNC_DEBOUNCE_MS
    );
  }

  function mutationOnlyTouchesStatus(records) {
    return records.length > 0 && records.every((record) => {
      const target =
        record.target && record.target.nodeType === 1
          ? record.target
          : record.target && record.target.parentElement;
      return (
        target &&
        target.closest &&
        target.closest(
          [
            '[data-cm-city-gift-status]',
            '.additional-checkout-buttons',
            '.cart__dynamic-checkout-buttons',
            '.shopify-payment-button',
            '[data-shopify="payment-button"]',
            '[data-shopify-buttoncontainer]',
            'shopify-accelerated-checkout',
            'shopify-accelerated-checkout-cart'
          ].join(', ')
        )
      );
    });
  }

  function discardObserverRecords() {
    state.observers.forEach(({ observer }) => observer.takeRecords());
  }

  function attachCartObserver(key, target, reason) {
    if (!target || !global.MutationObserver) return;
    const existing = state.observers.get(key);
    if (existing && existing.target === target) return;
    if (existing) existing.observer.disconnect();

    const observer = new global.MutationObserver((records) => {
      if (mutationOnlyTouchesStatus(records)) return;
      if (queueUnverifiedRenderedCartReview(state, reason, lockCheckout)) return;
      scheduleSync(reason);
    });
    observer.observe(target, { childList: true, subtree: true });
    state.observers.set(key, { observer, target });
  }

  function attachCartObservers() {
    attachCartObserver(
      'drawer',
      document.getElementById('CartDrawer'),
      'cart-drawer-render'
    );
    attachCartObserver(
      'main-cart',
      document.getElementById('main-cart-items'),
      'main-cart-render'
    );
  }

  function isCheckoutAction(target) {
    if (!target || !target.closest) return false;
    return Boolean(
      target.closest(
        [
          '#CartDrawer-Checkout',
          '#checkout',
          'a[href*="/checkout"]',
          '.additional-checkout-buttons',
          '.cart__dynamic-checkout-buttons',
          '.shopify-payment-button',
          '[data-shopify="payment-button"]',
          '[data-shopify-buttoncontainer]',
          'shopify-accelerated-checkout',
          'shopify-accelerated-checkout-cart'
        ].join(', ')
      )
    );
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!state.locked || !isCheckoutAction(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const status = statusElements()[0];
      if (status) {
        status.setAttribute('tabindex', '-1');
        status.focus();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!state.locked) return;
      const submitter = event.submitter;
      const form = event.target;
      const isCheckoutSubmit =
        (submitter &&
          (submitter.id === 'checkout' ||
            submitter.id === 'CartDrawer-Checkout' ||
            submitter.name === 'checkout')) ||
        (form &&
          typeof form.action === 'string' &&
          /\/checkout(?:[/?#]|$)/.test(form.action));
      if (!isCheckoutSubmit) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  function scheduleReverify(reason) { if (queueUnverifiedRenderedCartReview(state, reason, lockCheckout)) return; scheduleSync(reason, null, 0); } function start() {
    attachCartObservers();
    scheduleSync('initial-cart-read', null, 0);

    global.addEventListener('pageshow', () => scheduleReverify('pageshow'));
    global.addEventListener('online', () => scheduleReverify('online'));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        scheduleReverify('visibility-return');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})(typeof window !== 'undefined' ? window : null);
